import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronLeft,
  faChevronRight,
  faRotateRight,
  faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import usageFocusApi from "../../../api/analytics/usageFocusApi";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import { UsageExplorerViewToken } from "../../../types/analytics/UsageExplorerViewToken";
import {
  UsageFocusDto,
  UsageFocusPriority,
  UsageFocusRecommendationDto,
} from "../../../types/analytics/UsageFocusDto";
import {
  areExplorerViewTokensEqual,
  clearExplorerViewTokenFilter,
  sortFocusRecommendations,
  toExplorerViewToken,
} from "../../../helpers/usage/usageFocusHelpers";
import {
  GetMainScrollContainer,
  ScrollMainContainerToElement,
} from "../../layout/MainContainer";

type FocusState =
  | "IDLE"
  | "GENERATING"
  | "READY"
  | "RATE_LIMITED"
  | "NO_DATA"
  | "ERROR";

interface UsageFocusPanelProps {
  filters: AnalyticsFilterInputs | null;
  explorerViewToken: UsageExplorerViewToken | null;
  onExplorerViewChange: (token: UsageExplorerViewToken) => void;
  variant?: "section" | "embedded";
  generateOnMount?: boolean;
}

function UsageFocusPanel({
  filters,
  explorerViewToken,
  onExplorerViewChange,
  variant = "section",
  generateOnMount = false,
}: UsageFocusPanelProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const api = useMemo(
    () => new usageFocusApi(auth.user?.access_token),
    [auth.user?.access_token],
  );
  const [state, setState] = useState<FocusState>(
    generateOnMount && filters ? "GENERATING" : "IDLE",
  );
  const [focus, setFocus] = useState<UsageFocusDto | null>(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [retryAfterMinutes, setRetryAfterMinutes] = useState<number | null>(
    null,
  );
  const requestGenerationRef = useRef(0);
  const isFirstFilterEffectRef = useRef(true);
  const hasTriggeredInitialGenerateRef = useRef(false);

  const sortedRecommendations = useMemo(
    () => sortFocusRecommendations(focus?.recommendations ?? []),
    [focus],
  );

  useEffect(() => {
    if (isFirstFilterEffectRef.current) {
      isFirstFilterEffectRef.current = false;
      return;
    }

    requestGenerationRef.current += 1;
    setState("IDLE");
    setFocus(null);
    setActiveCardIndex(0);
    setRetryAfterMinutes(null);
  }, [filters]);

  useEffect(() => {
    setActiveCardIndex(0);
  }, [focus]);

  const generate = useCallback(
    async (refresh: boolean) => {
      if (!filters) return;

      const requestGeneration = ++requestGenerationRef.current;
      setState("GENERATING");

      const result = await api.generate(filters, refresh);

      if (requestGeneration !== requestGenerationRef.current) return;

      if (result.status === "OK") {
        setFocus(result.focus);
        setRetryAfterMinutes(null);
        setState("READY");
        return;
      }

      setFocus(null);
      setRetryAfterMinutes(
        result.status === "RATE_LIMITED" ? result.retryAfterMinutes : null,
      );
      setState(
        result.status === "RATE_LIMITED"
          ? "RATE_LIMITED"
          : result.status === "NO_DATA"
            ? "NO_DATA"
            : "ERROR",
      );
    },
    [api, filters],
  );

  useEffect(() => {
    if (!generateOnMount || hasTriggeredInitialGenerateRef.current) return;

    hasTriggeredInitialGenerateRef.current = true;
    generate(false);
  }, [generate, generateOnMount]);

  const isEmbedded = variant === "embedded";
  const showResultLayout =
    state === "GENERATING" || state === "READY" || state === "NO_DATA";

  return (
    <div
      className={cx(isEmbedded ? "mt-4 w-full" : "mx-3 mt-6 border-t border-gray-200 pt-5")}
      aria-live="polite"
    >
      {!isEmbedded && (
        <h2 className="mb-4 font-heading text-xl font-semibold text-gray-700">
          {t(
            "Analytics.Widgets.Usage.Focus.TeaserHeading",
            "Improve completion with AI",
          )}
        </h2>
      )}

      {state === "IDLE" && (
        <FocusIdleRow
          onGenerate={() => generate(false)}
          disabled={!filters}
        />
      )}

      {(state === "ERROR" || state === "RATE_LIMITED") && (
        <FocusInlineMessageRow
          message={
            state === "RATE_LIMITED"
              ? t(
                  "Analytics.Widgets.Usage.Focus.RateLimited",
                  "To keep AI usage fair, you have reached the generation limit. Try again in about {{count}} minutes.",
                  { count: retryAfterMinutes ?? 1 },
                )
              : t(
                  "Analytics.Widgets.Usage.Focus.Error",
                  "This summary could not be generated just now. The rest of the page is unaffected.",
                )
          }
          tone={state === "RATE_LIMITED" ? "WARNING" : "ERROR"}
          onRetry={() => generate(false)}
        />
      )}

      {showResultLayout && (
        <div className="grid gap-4 md:grid-cols-5 md:gap-6">
          <div className="md:col-span-2">
            {state === "GENERATING" ? (
              <FocusNarrativeSkeleton />
            ) : state === "NO_DATA" ? (
              <FocusMessage
                tone="NEUTRAL"
                message={t(
                  "Analytics.Widgets.Usage.Focus.NoData",
                  "There are no assignments in this selection to summarise.",
                )}
              />
            ) : (
              focus && (
                <FocusNarrativeColumn
                  focus={focus}
                  onRefresh={() => generate(true)}
                  isRefreshing={false}
                />
              )
            )}
          </div>

          <div className="md:col-span-3">
            {state === "GENERATING" ? (
              <FocusCardSkeleton />
            ) : state === "READY" && focus ? (
              <FocusRecommendationPager
                recommendations={sortedRecommendations}
                activeCardIndex={activeCardIndex}
                onActiveCardIndexChange={setActiveCardIndex}
                explorerViewToken={explorerViewToken}
                onExplorerViewChange={onExplorerViewChange}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function FocusIdleRow({
  onGenerate,
  disabled,
}: {
  onGenerate: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-gray-600">
        {t(
          "Analytics.Widgets.Usage.Focus.IdlePrompt",
          "Get AI suggestions on where to focus, based on the current filters.",
        )}
      </p>
      <button
        type="button"
        className="btn-secondary btn-small inline-flex shrink-0 items-center gap-2 self-start sm:self-auto"
        onClick={onGenerate}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faWandMagicSparkles} aria-hidden />
        {t("Analytics.Widgets.Usage.Focus.Generate", "Generate")}
      </button>
    </div>
  );
}

function FocusInlineMessageRow({
  message,
  tone,
  onRetry,
}: {
  message: string;
  tone: "WARNING" | "ERROR";
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p
        className={cx(
          "text-sm",
          tone === "WARNING" ? "text-amber-800" : "text-gray-700",
        )}
      >
        {message}
      </p>
      {tone === "ERROR" && (
        <button
          type="button"
          className="btn-secondary btn-small shrink-0 self-start sm:self-auto"
          onClick={onRetry}
        >
          {t("Analytics.Widgets.Usage.Focus.Retry", "Try again")}
        </button>
      )}
    </div>
  );
}

function FocusNarrativeSkeleton() {
  return (
    <div className="animate-pulse space-y-3" aria-hidden>
      <div className="h-4 w-full rounded bg-gray-200" />
      <div className="h-4 w-full rounded bg-gray-200" />
      <div className="h-4 w-3/4 rounded bg-gray-200" />
      <div className="mt-6 h-3 w-2/3 rounded bg-gray-100" />
      <div className="h-8 w-24 rounded bg-gray-100" />
    </div>
  );
}

function FocusCardSkeleton() {
  return (
    <div
      className="animate-pulse rounded-lg border border-gray-200 bg-white p-5"
      aria-hidden
    >
      <div className="flex items-start justify-between gap-3">
        <div className="h-5 w-2/3 rounded bg-gray-200" />
        <div className="h-5 w-16 rounded bg-gray-100" />
      </div>
      <div className="mt-4 h-4 w-full rounded bg-gray-100" />
      <div className="mt-2 h-4 w-5/6 rounded bg-gray-100" />
      <div className="mt-4 h-4 w-1/2 rounded bg-gray-100" />
      <div className="mt-6 h-8 w-32 rounded bg-gray-200" />
    </div>
  );
}

function FocusNarrativeColumn({
  focus,
  onRefresh,
  isRefreshing,
}: {
  focus: UsageFocusDto;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <p className="text-sm text-gray-700">{focus.narrative}</p>
      <p className="mt-3 text-xs text-gray-500">
        {focus.fromCache
          ? t(
              "Analytics.Widgets.Usage.Focus.Cached",
              "Written for these same figures. Refresh to generate again.",
            )
          : t(
              "Analytics.Widgets.Usage.Focus.Fresh",
              "Generated from the figures shown above. Check the underlying rows before acting.",
            )}
      </p>
      <button
        type="button"
        className="btn-secondary btn-small mt-3 inline-flex items-center gap-2"
        onClick={onRefresh}
        disabled={isRefreshing}
      >
        <FontAwesomeIcon icon={faRotateRight} aria-hidden />
        {t("Analytics.Widgets.Usage.Focus.Refresh", "Refresh")}
      </button>
    </div>
  );
}

function FocusRecommendationPager({
  recommendations,
  activeCardIndex,
  onActiveCardIndexChange,
  explorerViewToken,
  onExplorerViewChange,
}: {
  recommendations: UsageFocusRecommendationDto[];
  activeCardIndex: number;
  onActiveCardIndexChange: (index: number) => void;
  explorerViewToken: UsageExplorerViewToken | null;
  onExplorerViewChange: (token: UsageExplorerViewToken) => void;
}) {
  const { t } = useTranslation();
  const total = recommendations.length;
  const safeIndex = total === 0 ? 0 : Math.min(activeCardIndex, total - 1);
  const activeRecommendation = total > 0 ? recommendations[safeIndex] : null;
  const showPager = total > 1;
  const canGoPrev = safeIndex > 0;
  const canGoNext = safeIndex < total - 1;

  if (!activeRecommendation) {
    return null;
  }

  return (
    <div>
      <RecommendationCard
        recommendation={activeRecommendation}
        explorerViewToken={explorerViewToken}
        onExplorerViewChange={onExplorerViewChange}
      />

      {showPager && (
        <div className="mt-3 flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => onActiveCardIndexChange(Math.max(0, safeIndex - 1))}
            disabled={!canGoPrev}
            aria-label={t(
              "Analytics.Widgets.Usage.Focus.PreviousRecommendation",
              "Previous recommendation",
            )}
            className={cx(
              "inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]",
              canGoPrev
                ? "text-gray-600 hover:bg-gray-50"
                : "cursor-not-allowed text-gray-300",
            )}
          >
            <FontAwesomeIcon icon={faChevronLeft} aria-hidden />
          </button>
          <span
            className="min-w-[4.5rem] text-center text-sm tabular-nums text-gray-600"
            aria-live="polite"
            aria-atomic="true"
          >
            {t(
              "Analytics.Widgets.Usage.Focus.RecommendationPosition",
              "{{current}} of {{total}}",
              { current: safeIndex + 1, total },
            )}
          </span>
          <button
            type="button"
            onClick={() =>
              onActiveCardIndexChange(Math.min(total - 1, safeIndex + 1))
            }
            disabled={!canGoNext}
            aria-label={t(
              "Analytics.Widgets.Usage.Focus.NextRecommendation",
              "Next recommendation",
            )}
            className={cx(
              "inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]",
              canGoNext
                ? "text-gray-600 hover:bg-gray-50"
                : "cursor-not-allowed text-gray-300",
            )}
          >
            <FontAwesomeIcon icon={faChevronRight} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

function RecommendationCard({
  recommendation,
  explorerViewToken,
  onExplorerViewChange,
}: {
  recommendation: UsageFocusRecommendationDto;
  explorerViewToken: UsageExplorerViewToken | null;
  onExplorerViewChange: (token: UsageExplorerViewToken) => void;
}) {
  const { t } = useTranslation();
  const recommendationToken = toExplorerViewToken(recommendation.viewToken);
  const isApplied = areExplorerViewTokensEqual(
    explorerViewToken,
    recommendationToken,
  );

  const showInTable = () => {
    onExplorerViewChange(recommendationToken);
    requestAnimationFrame(() => {
      const tableHeader = document.getElementById(
        "usage-explorer-table-anchor",
      );
      const mainContainer = GetMainScrollContainer();
      if (!tableHeader || !mainContainer) return;

      const headerRect = tableHeader.getBoundingClientRect();
      const containerRect = mainContainer.getBoundingClientRect();
      const headerIsVisible =
        headerRect.top >= containerRect.top &&
        headerRect.bottom <= containerRect.bottom;

      if (!headerIsVisible) {
        ScrollMainContainerToElement(tableHeader);
      }
    });
  };

  const clearFromTable = () => {
    onExplorerViewChange(clearExplorerViewTokenFilter(explorerViewToken));
  };

  return (
    <div className="flex flex-col rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-heading text-base font-semibold text-gray-800">
          {recommendation.title}
        </h3>
        <PriorityBadge priority={recommendation.priority} />
      </div>

      {recommendation.detail && (
        <p className="mt-2 text-sm text-gray-600">{recommendation.detail}</p>
      )}

      {recommendation.action && (
        <p className="mt-2 text-sm font-medium text-gray-700">
          {recommendation.action}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {isApplied ? (
          <>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
              <FontAwesomeIcon icon={faCheck} className="text-primary" aria-hidden />
              {t("Analytics.Widgets.Usage.Focus.ShownInTable", "Shown in table")}
            </span>
            <button
              type="button"
              className="text-sm font-semibold text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              onClick={clearFromTable}
            >
              {t("Common.Clear", "Clear")}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
            onClick={showInTable}
          >
            {t("Analytics.Widgets.Usage.Focus.ShowInTable", "Show in table")}
          </button>
        )}
      </div>
    </div>
  );
}

function FocusMessage({
  tone,
  message,
}: {
  tone: "WARNING" | "NEUTRAL";
  message: string;
}) {
  return (
    <p
      className={cx(
        "rounded-md border p-4 text-sm",
        tone === "WARNING"
          ? "border-amber-200 bg-amber-50 text-amber-800"
          : "border-gray-200 bg-gray-50 text-gray-700",
      )}
    >
      {message}
    </p>
  );
}

function PriorityBadge({ priority }: { priority: UsageFocusPriority }) {
  const { t } = useTranslation();

  const label =
    priority === "HIGH"
      ? t("Analytics.Widgets.Usage.Focus.Priority.High", "High")
      : priority === "LOW"
        ? t("Analytics.Widgets.Usage.Focus.Priority.Low", "Low")
        : t("Analytics.Widgets.Usage.Focus.Priority.Medium", "Medium");

  return (
    <span
      className={cx(
        "shrink-0 rounded px-2 py-0.5 text-xs font-medium",
        priority === "HIGH"
          ? "bg-amber-100 text-amber-800"
          : "bg-gray-100 text-gray-700",
      )}
    >
      {t("Analytics.Widgets.Usage.Focus.Priority.Label", "{{priority}} priority", {
        priority: label,
      })}
    </span>
  );
}

export default UsageFocusPanel;
